/**
 * Platform Admin → Command Runner.
 *
 * Runs registered management commands from the browser and shows their output.
 * Two gates decide what appears and what can run:
 *
 *  - the backend registry decides what is runnable from the web at all;
 *  - `PlatformSettings.enabled_commands` decides what is runnable on THIS
 *    install, toggled here. One Heroku app per tenant, so that switch is how a
 *    command is scoped to particular customers.
 *
 * Runs execute on a Celery worker and are polled: these commands take longer
 * than Heroku's 30-second router timeout, and the operator needs the printed
 * report, not just an exit code.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle, CheckCircle2, ChevronRight, Clock, Loader2, RefreshCw, Terminal, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import {
  commandRunnerAPI, apiErrorMessage,
  type CommandRun, type RunnableCommandSpec, type CommandResultHandoff,
} from '../../../lib/api';
import { CommandRunForm } from './CommandRunForm';
import { CommandRunResult } from './CommandRunResult';

/** How often a queued/running job is polled. */
const POLL_MS = 2000;

function StatusBadge({ status }: { status: CommandRun['status'] }) {
  const map = {
    queued: { label: 'Queued', className: 'bg-slate-100 text-slate-700', Icon: Clock },
    running: { label: 'Running', className: 'bg-blue-100 text-blue-700', Icon: Loader2 },
    success: { label: 'Success', className: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
    failed: { label: 'Failed', className: 'bg-red-100 text-red-700', Icon: XCircle },
  } as const;
  const { label, className, Icon } = map[status];
  return (
    <Badge className={`${className} gap-1 font-medium`} variant="secondary">
      <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {label}
    </Badge>
  );
}

export function CommandRunnerTab() {
  const [commands, setCommands] = useState<RunnableCommandSpec[]>([]);
  const [selected, setSelected] = useState<RunnableCommandSpec | null>(null);
  const [runs, setRuns] = useState<CommandRun[]>([]);
  const [activeRun, setActiveRun] = useState<CommandRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [prefill, setPrefill] = useState<Record<string, string | number | boolean> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadCommands = useCallback(async () => {
    try {
      const res = await commandRunnerAPI.getCommands();
      setCommands(res.data.commands);
      setError(null);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Could not load the command list'));
    }
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const res = await commandRunnerAPI.getRuns({ page_size: 15 });
      setRuns(res.data.results ?? []);
    } catch {
      // History is supporting detail — a failure here must not blank the page.
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadCommands(), loadRuns()]);
      setLoading(false);
    })();
  }, [loadCommands, loadRuns]);

  // Poll while a run is unfinished; stop as soon as it settles.
  useEffect(() => {
    if (!activeRun || activeRun.is_finished) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const res = await commandRunnerAPI.getRun(activeRun.id);
        setActiveRun(res.data);
        if (res.data.is_finished) { loadRuns(); }
      } catch {
        // Transient poll failure: keep the last known state and try again.
      }
    }, POLL_MS);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [activeRun, loadRuns]);

  const handleToggle = useCallback(async (cmd: RunnableCommandSpec, enabled: boolean) => {
    try {
      await commandRunnerAPI.toggleCommand(cmd.key, enabled);
      setCommands((prev) => prev.map(
        (c) => (c.key === cmd.key ? { ...c, enabled } : c),
      ));
      if (selected?.key === cmd.key) setSelected({ ...cmd, enabled });
      toast.success(`${cmd.label} ${enabled ? 'enabled' : 'disabled'} on this install`);
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Could not change that setting'));
    }
  }, [selected]);

  /** Load one command's findings into another command's arguments. */
  const handleHandoff = useCallback((handoff: CommandResultHandoff) => {
    const target = commands.find((c) => c.key === handoff.command);
    if (!target) {
      toast.error(`${handoff.command} is not registered on this install`);
      return;
    }
    if (!target.enabled) {
      toast.error(`${target.label} is not enabled here — switch it on first`);
      return;
    }
    setPrefill({
      ...(handoff.carry ?? {}),
      [handoff.field]: handoff.values.join(','),
    });
    setSelected(target);
    toast.success(
      `${handoff.values.length.toLocaleString()} id(s) loaded into ${target.label}`,
    );
  }, [commands]);

  const handleRun = useCallback(async (args: Record<string, string | number | boolean>) => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const res = await commandRunnerAPI.createRun(selected.key, args);
      setActiveRun(res.data);
      loadRuns();
      toast.success('Run queued');
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Could not queue that run'));
    } finally {
      setSubmitting(false);
    }
  }, [selected, loadRuns]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-8 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading commands…
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="flex items-center gap-3 p-6 text-red-600">
          <AlertCircle className="h-5 w-5" />
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Command Runner</h2>
        <p className="text-sm text-muted-foreground">
          Run a registered management command on this install. Only commands in
          the backend registry appear here, and each must be switched on before
          it can run — that switch is per install, so it is how a command is
          scoped to a particular customer.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Command list + per-install enablement */}
        <Card className="col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Available commands</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {commands.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No commands are registered on this build.
              </p>
            )}
            {commands.map((cmd) => (
              <div
                key={cmd.key}
                className={`rounded-md border p-3 ${
                  selected?.key === cmd.key ? 'border-primary bg-primary/5' : 'border-slate-200'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => { setPrefill(null); setSelected(cmd); }}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center gap-1 text-sm font-medium">
                      {cmd.label}
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <code className="text-[11px] text-muted-foreground">{cmd.key}</code>
                  </button>
                  <Switch
                    checked={cmd.enabled}
                    onCheckedChange={(v) => handleToggle(cmd, v)}
                    aria-label={`Enable ${cmd.label} on this install`}
                  />
                </div>
                {!cmd.enabled && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Not enabled here — switch on to run it.
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Selected command: description, arguments, run */}
        <Card className="col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              {selected ? selected.label : 'Select a command'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selected ? (
              <p className="text-sm text-muted-foreground">
                Pick a command on the left to see what it does and set its arguments.
              </p>
            ) : !selected.enabled ? (
              <div className="flex items-start gap-2 text-sm text-amber-700">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <span>
                  <strong>{selected.label}</strong> is not enabled on this install.
                  Switch it on to run it.
                </span>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{selected.description}</p>
                <CommandRunForm
                  key={`${selected.key}:${prefill ? 'prefilled' : 'blank'}`}
                  command={selected}
                  submitting={submitting}
                  prefill={prefill ?? undefined}
                  onRun={handleRun}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Live output of the run just queued */}
      {activeRun && (
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Terminal className="h-4 w-4" />
              {activeRun.command_label}
              <StatusBadge status={activeRun.status} />
              {activeRun.duration_seconds !== null && (
                <span className="text-xs font-normal text-muted-foreground">
                  {activeRun.duration_seconds.toFixed(1)}s
                </span>
              )}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const res = await commandRunnerAPI.getRun(activeRun.id);
                setActiveRun(res.data);
              }}
            >
              <RefreshCw className="h-3 w-3 mr-1" /> Refresh
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {activeRun.error && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {activeRun.error}
              </div>
            )}
            {!activeRun.is_finished && !activeRun.stdout && (
              <p className="text-sm text-muted-foreground">
                {activeRun.status === 'queued'
                  ? 'Queued — waiting for a worker to pick it up.'
                  : 'Running…'}
              </p>
            )}
            {(activeRun.stdout || activeRun.result) && (
              <CommandRunResult
                result={activeRun.result ?? {}}
                stdout={activeRun.stdout}
                onHandoff={handleHandoff}
              />
            )}
            {activeRun.stderr && (
              <div>
                <p className="text-xs font-medium text-red-600 mb-1">stderr</p>
                <pre className="max-h-64 overflow-auto rounded-md bg-red-950 p-3 text-xs text-red-100 whitespace-pre-wrap">
                  {activeRun.stderr}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Recent runs</CardTitle>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing has been run yet.</p>
          ) : (
            <div className="space-y-1">
              {runs.map((run) => (
                <button
                  key={run.id}
                  type="button"
                  onClick={() => setActiveRun(run)}
                  className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm hover:bg-accent"
                >
                  <StatusBadge status={run.status} />
                  <span className="font-medium">{run.command_label}</span>
                  <span className="text-xs text-muted-foreground truncate flex-1">
                    {Object.entries(run.arguments)
                      .map(([k, v]) => `${k}=${v}`)
                      .join('  ') || 'no arguments'}
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {run.requested_by_name} · {new Date(run.created_at).toLocaleString()}
                  </span>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
